import React, { memo, useEffect, useState } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import {
  CheckBox,
  color,
  FormGroup,
  Icon,
  Input,
  Modal,
  spacing,
  textStyleButton,
  Tooltip
} from '@uniquegood/realworld-studio-design';
import { useSWRConfig } from 'swr';

import { coreApi } from '@/api';
import {
  ButtonResetStyle,
  Flex,
  KeyboardFocusStyle,
  MarginBottom16,
  MarginBottom24
} from '@/styles';
import { onMobile } from '@/styles/responsive';
import { QuestButton } from '@/pages/missions/components/missionList';
import MissionListContextMenu from '@/pages/missions/components/missionList/MissionListContextMenu';
import { IReOrder, QuestRequestBody } from '@/pages/missions/types';
import {
  buildQuestLabel,
  duplicateQuest,
  extractQuestResponseData,
  MISSION_API_VERSION_HEADER,
  MISSION_NAV_CARD_WIDTH
} from '@/pages/missions/utils';
import { toast, invalidFieldBoilerplate, reorder } from '@/utils';
import { useDidUpdateEffect, useModalState } from '@/hooks';

import { QuestModalImage } from '../../assets';
import TooltipContent from '../TooltipContent';
import { SaveEventName, track } from '@/track';
import { validateCustomButtonLabel } from '@/pages/missions/questValidation';

interface DraggableQuestListProps {
  quests: Quest[];
  mission: Mission;
  boundaryElement?: HTMLElement | null;
}

interface QuestContextMenuState {
  x: number;
  y: number;
  quest: Quest;
}

type FormData = QuestRequestBody & { isCustomizeButtonText: boolean };

function DraggableQuestList({ quests, mission, boundaryElement }: DraggableQuestListProps) {
  const { appId, projectId, scenarioId, questId } = useParams<AppParam>();
  const history = useHistory();

  const { mutate } = useSWRConfig();

  const missionKey = `/apps/${appId}/scenarios/${scenarioId}/missions`;

  const { control, handleSubmit, reset, formState, setError, clearErrors } = useForm<FormData>({
    defaultValues: {
      description: '',
      isCustomizeButtonText: false,
      isVisibleDefault: true,
      isLockedDefault: false
    }
  });
  const { modal, openModal, closeModal } = useModalState();

  const [_quests, setQuests] = useState<Quest[]>(quests);
  const [duplicatingQuestId, setDuplicatingQuestId] = useState<number | null>(null);
  const [deletingQuestId, setDeletingQuestId] = useState<number | null>(null);
  const [contextMenu, setContextMenu] = useState<QuestContextMenuState | null>(null);

  // useEffect(() => {
  //   if (quests) {
  //     sessionStorage.setItem('questSessionData', JSON.stringify(quests));
  //   }
  // }, [quests]);

  // useEffect(() => {
  //   if (mission) {
  //     sessionStorage.setItem('missionSessionData', JSON.stringify(mission));
  //   }
  // }, [mission]);

  const onDragEndQuests = ({ destination, source }: DropResult) => {
    if (!destination || !_quests) return;

    const newQuests = reorder(_quests, source.index, destination.index);

    setQuests(newQuests);

    handleChangeQuestOrder({ from: source.index, to: destination.index });
  };

  const handleChangeQuestOrder = async (data: IReOrder) => {
    await coreApi.put(
      `/apps/${appId}/missions/${mission.id}/quests/order`,
      data,
      MISSION_API_VERSION_HEADER
    );
    await mutate(missionKey);
  };

  const onSubmit = handleSubmit(async ({ isCustomizeButtonText, ...data }) => {
    clearErrors('label');

    const customButtonLabelError = validateCustomButtonLabel({
      isCustomizeButtonText,
      label: data.label
    });
    if (customButtonLabelError) {
      setError(
        'label',
        { type: 'required', message: customButtonLabelError },
        { shouldFocus: true }
      );
      return;
    }

    const payload = {
      name: data.name,
      description: data.description ?? '',
      label: buildQuestLabel({
        name: data.name,
        label: data.label,
        isCustomizeButtonText
      }),
      isVisibleDefault: data.isVisibleDefault ?? true,
      isLockedDefault: data.isLockedDefault ?? false
    };

    const { data: createdQuestResponse } = await coreApi.post<
      QuestResponseModel | CommonResponseModel<QuestResponseModel>
    >(`/apps/${appId}/missions/${mission.id}/quests`, payload, MISSION_API_VERSION_HEADER);
    const createdQuest = extractQuestResponseData<QuestResponseModel>(createdQuestResponse);
    track.onSave({ saveEventName: SaveEventName.save_editgame_mission_new_quest });
    await mutate(missionKey);
    closeModal();

    if (!createdQuest) {
      toast({ message: '생성된 퀘스트 정보를 불러오지 못했어요' });
      return;
    }

    history.push(
      `/apps/${appId}/projects/${projectId}/scenarios/${scenarioId}/missions/${mission.id}/quests/${createdQuest.id}`
    );
    toast({ message: '퀘스트를 생성했어요' });
  });

  const openCreateQuestModal = () => {
    openModal({
      size: 'medium',
      title: '퀘스트 생성',
      cssStyle: QuestModalLayout,
      children: (
        <CreateQuestContent control={control} clearErrors={clearErrors} onSubmit={onSubmit} />
      ),
      primaryAction: {
        content: '저장',
        icon: 'save_solid',
        htmlType: 'submit',
        form: 'modal',
        loading: formState.isSubmitting,
        disabled: formState.isSubmitting
      },
      secondaryAction: {
        content: '닫기',
        onAction: closeModal
      }
    });
  };

  const onDuplicateQuest = async (quest: Quest) => {
    if (duplicatingQuestId !== null) return;

    setDuplicatingQuestId(quest.id);

    try {
      const createdQuest = await duplicateQuest({
        appId,
        missionId: mission.id,
        quest
      });

      await mutate(missionKey);
      history.push(
        `/apps/${appId}/projects/${projectId}/scenarios/${scenarioId}/missions/${mission.id}/quests/${createdQuest.id}`
      );
      toast({ message: '퀘스트를 복제했어요' });
    } finally {
      setDuplicatingQuestId(null);
    }
  };

  const openQuestContextMenu = (event: React.MouseEvent, quest: Quest) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      quest
    });
  };

  const onDeleteQuest = (quest: Quest) => {
    openModal({
      size: 'small',
      title: '퀘스트를 삭제하시겠어요?',
      children: '삭제된 퀘스트는 다시 복구할 수 없어요',
      primaryAction: {
        content: '삭제',
        type: 'destructive',
        icon: 'trash_alt_solid',
        loading: deletingQuestId === quest.id,
        disabled: deletingQuestId === quest.id,
        onAction: async () => {
          setDeletingQuestId(quest.id);

          try {
            await coreApi.delete(`/apps/${appId}/quests/${quest.id}`, MISSION_API_VERSION_HEADER);
            await mutate(missionKey);

            if (questId === quest.id.toString()) {
              history.push(
                `/apps/${appId}/projects/${projectId}/scenarios/${scenarioId}/missions/${mission.id}`
              );
            }
            toast({ message: '퀘스트를 삭제했어요' });
          } finally {
            setDeletingQuestId(null);
            closeModal();
          }
        }
      },
      secondaryAction: {
        content: '취소',
        onAction: closeModal
      }
    });
  };

  useDidUpdateEffect(() => {
    setQuests(quests);
  }, [quests]);

  useEffect(() => {
    if (!modal.open) {
      reset();
    }
  }, [modal.open]);

  const isModalSubmitting = formState.isSubmitting || deletingQuestId !== null;
  const modalPrimaryAction = modal.primaryAction
    ? {
        ...modal.primaryAction,
        loading: isModalSubmitting,
        disabled: isModalSubmitting
      }
    : undefined;

  return (
    <div css={QuestButtonGroupStyle}>
      <Modal {...modal} primaryAction={modalPrimaryAction} />

      <DragDropContext onDragEnd={onDragEndQuests}>
        <Droppable droppableId="quest-list">
          {(provided) => (
            <ul ref={provided.innerRef} {...provided.droppableProps}>
              {_quests.map((quest, index) => {
                const isQuestSelected = questId === quest.id.toString();

                return (
                  <QuestButton
                    key={quest.id}
                    mission={mission}
                    quest={quest}
                    index={index}
                    isHidden={!quest.isVisibleDefault}
                    isLocked={quest.isLockedDefault}
                    selected={isQuestSelected}
                    onContextMenu={openQuestContextMenu}
                  />
                );
              })}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
      {contextMenu && (
        <MissionListContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          boundaryElement={boundaryElement}
          onClose={() => setContextMenu(null)}
          items={[
            {
              label: '퀘스트 복제',
              icon: 'copy_solid',
              disabled: duplicatingQuestId === contextMenu.quest.id,
              onClick: () => onDuplicateQuest(contextMenu.quest)
            },
            {
              label: '퀘스트 삭제',
              icon: 'trash_alt_solid',
              destructive: true,
              disabled: deletingQuestId === contextMenu.quest.id,
              onClick: () => onDeleteQuest(contextMenu.quest)
            }
          ]}
        />
      )}

      <button
        type="button"
        onClick={openCreateQuestModal}
        css={[ButtonResetStyle, QuestButtonStyle, textStyleButton, KeyboardFocusStyle]}
      >
        <Icon icon="plus_solid" size="16px" color={color.action_primary_default} />새 퀘스트
      </button>
    </div>
  );
}

export default memo(DraggableQuestList);

const QuestButtonGroupStyle = css`
  display: flex;
  flex-direction: column;
  width: ${MISSION_NAV_CARD_WIDTH};

  ${onMobile} {
    margin-left: 12px;
  }
`;

const QuestButtonStyle = css`
  margin-top: ${spacing.common.small};
  padding: ${spacing.common.small} 12px;
  border-radius: ${spacing.common.medium2};
  background-color: ${color.surface_default_default};
  color: ${color.text_default};
  width: 100%;
  display: flex;
  align-items: center;

  &:hover {
    background-color: ${color.surface_default_hovered};
  }
  &:active {
    background-color: ${color.surface_default_pressed};
  }

  & > .IconWrap {
    margin: 0px;
    margin-right: ${spacing.common.xsmall};
  }

  color: ${color.action_primary_default};

  &:disabled {
    background-color: ${color.surface_default_default};
    cursor: not-allowed;
    & > span > svg,
    p {
      color: ${color.text_disabled};
    }
  }
`;

const QuestModalLayout = css`
  & .preview-img {
    width: 183px;
    height: 293px;
    object-fit: cover;
  }

  ${onMobile} {
    & .preview-img {
      display: none;
    }
  }
`;

const QuestModalFormGroup = css`
  margin-left: ${spacing.common.medium};
  width: 100%;

  ${onMobile} {
    margin-left: 0px;
  }
`;

const CheckBoxWrapStyle = css`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing.common.small};

  & > span:last-of-type {
    margin-left: ${spacing.common.xsmall};
  }

  & > div {
    width: 100%;
  }
`;

const QuestButtonTextWrapStyle = css`
  border-radius: ${spacing.borderRadius.medium};
`;

const ToggleWrapStyle = css`
  display: flex;
  & > div {
    & > div {
      margin-bottom: ${spacing.common.small};
      & > span:first-of-type {
        margin-right: ${spacing.common.small};
      }
    }
    width: 50%;
  }

  ${onMobile} {
    display: initial;

    & > div {
      &:first-of-type {
        margin-bottom: ${spacing.common.medium};
      }
      width: 100%;
    }
  }
`;

interface CreateQuestContentProps extends Pick<UseFormReturn<FormData>, 'control' | 'clearErrors'> {
  onSubmit: React.FormEventHandler<HTMLFormElement>;
}

function CreateQuestContent({ control, clearErrors, onSubmit }: CreateQuestContentProps) {
  return (
    <form id="modal" onSubmit={onSubmit}>
      <div css={[Flex, MarginBottom24]}>
        <img alt="퀘스트 프리뷰" src={QuestModalImage.preview} className="preview-img" />
        <div css={QuestModalFormGroup}>
          <Controller
            control={control}
            name="name"
            rules={{ required: '퀘스트 이름' }}
            render={({ field: { value, onChange }, fieldState: { invalid } }) => (
              <FormGroup
                cssStyle={MarginBottom16}
                errorText={invalidFieldBoilerplate(invalid)}
                label="퀘스트 이름"
                requiredIndicator
              >
                <Input value={value ?? ''} onChange={onChange} error={invalid} />
              </FormGroup>
            )}
          />
          <FormGroup cssStyle={MarginBottom16} label="퀘스트 설명">
            <Controller
              control={control}
              name="description"
              render={({ field: { value, onChange } }) => (
                <Input multiline value={value ?? ''} onChange={onChange} />
              )}
            />
          </FormGroup>

          <Controller
            control={control}
            name="isCustomizeButtonText"
            render={({ field: { value, onChange } }) => (
              <>
                <div css={CheckBoxWrapStyle}>
                  <CheckBox
                    checked={value}
                    onChange={(checked) => {
                      clearErrors('label');
                      onChange(checked);
                    }}
                  >
                    퀘스트 버튼 문구를 다르게 설정
                  </CheckBox>
                  <Tooltip
                    content={
                      <TooltipContent
                        title="퀘스트 버튼 문구를 다르게 설정"
                        description="퀘스트 화면 상단에 보이는 '퀘스트 이름'과, 미션 화면에서 보이는 '퀘스트 버튼'의 문구를 다르게 설정하고 싶을 때 사용해요."
                        src={QuestModalImage.label}
                      />
                    }
                  >
                    <Icon icon="question_circle_solid" color={color.icon_disabled} />
                  </Tooltip>
                </div>
                {value && (
                  <div css={QuestButtonTextWrapStyle}>
                    <Controller
                      control={control}
                      name="label"
                      render={({ field: { value, onChange }, fieldState: { invalid, error } }) => (
                        <FormGroup
                          label="퀘스트 버튼 문구"
                          errorText={error?.message ?? invalidFieldBoilerplate(invalid)}
                        >
                          <Input
                            value={value ?? ''}
                            onChange={(label) => {
                              clearErrors('label');
                              onChange(label);
                            }}
                            error={invalid}
                          />
                        </FormGroup>
                      )}
                    />
                  </div>
                )}
              </>
            )}
          />
        </div>
      </div>
      <div css={ToggleWrapStyle}>
        <FormGroup
          label="퀘스트 버튼 기본 표시 여부"
          labelTooltip={{
            content: (
              <TooltipContent
                title="퀘스트 버튼 표시 / 미표시"
                description="미션 화면에서 퀘스트 버튼의 공개 여부를 설정해요"
                src={QuestModalImage.visible}
              />
            )
          }}
        >
          <Controller
            control={control}
            name="isVisibleDefault"
            defaultValue
            render={({ field: { value, onChange } }) => (
              <CheckBox onChange={onChange} checked={value}>
                표시
              </CheckBox>
            )}
          />
        </FormGroup>
        <FormGroup
          label="퀘스트 버튼 기본 잠금 여부"
          labelTooltip={{
            content: (
              <TooltipContent
                title="퀘스트 버튼 잠금 / 미잠금"
                description="미션 화면에서 퀘스트 버튼의 잠금 여부를 설정할 수 있어요"
                src={QuestModalImage.locked}
              />
            )
          }}
        >
          <Controller
            control={control}
            name="isLockedDefault"
            defaultValue={false}
            render={({ field: { value, onChange } }) => (
              <CheckBox onChange={onChange} checked={value}>
                잠금
              </CheckBox>
            )}
          />
        </FormGroup>
      </div>
    </form>
  );
}
